import React, { useState, useEffect } from "react";
import { Nav, Card, Badge } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { CommonButton } from "../Input";
import SendTransactionModal from "../Transactions/SendTransactionModal";
import SendBulkTransactionModal from "../Transactions/SendBulkTransactionModal";
import TopUpModal from "./TopUpModal";
import WithdrawalModal from "./WithdrawalModal";
import TransactionDetailsModal from "../Transactions/TransactionDetailsModal";

// Sub-components
import InfoTab from "./CustomerDetails/InfoTab";
import BeneficiaryTab from "./CustomerDetails/BeneficiaryTab";
import HistoryTab from "./CustomerDetails/HistoryTab";
import IndividualCustomerModal from "./IndividualCustomerModal";
import OrganizationCustomerModal from "./OrganizationCustomerModal";

const CustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("information");
  const [showSendTransactionModal, setShowSendTransactionModal] =
    useState(false);
  const [showSendBulkTransactionModal, setShowSendBulkTransactionModal] =
    useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [showTransactionDetailsModal, setShowTransactionDetailsModal] =
    useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showIndividualModal, setShowIndividualModal] = useState(false);
  const [showOrganizationModal, setShowOrganizationModal] = useState(false);

  // Mock data for finding the customer (in real app, this would be an API call)
  const mockCustomers = [
    {
      id: "10844",
      name: "SparkMinds Technology Pte Ltd",
      type: "Organization",
      email: "checkpast12month@yopmail.com",
      phone: "+84 765161894",
      idCard: "S1234567A013",
      availableBalance: "200 SGD\n0 USD",
      pendingBalance: "0 SGD\n0 USD",
      status: "ACTIVATED",
      kycStatus: "APPROVED",
      createdTime: "06/03/2026\n18:23:59",
    },
    {
      id: "10843",
      name: "Thuy Nga Phan",
      type: "Individual",
      email: "individual@yopmail.com",
      phone: "+84 789908092",
      idCard: "check12month",
      availableBalance: "969 SGD\n0 USD",
      pendingBalance: "7,031.05 SGD\n0 USD",
      status: "ACTIVATED",
      kycStatus: "APPROVED",
      createdTime: "06/03/2026\n11:38:29",
    },
    {
      id: "10842",
      name: "idjaoida",
      type: "Organization",
      email: "judhuiajhdoa@yopmail.com",
      phone: "+65 93213213",
      idCard: "154651651",
      availableBalance: "-",
      pendingBalance: "-",
      status: "ACTIVATED",
      kycStatus: "PENDING",
      createdTime: "05/03/2026\n10:41:31",
    },
  ];

  const customer = mockCustomers.find((c) => c.id === id) || mockCustomers[0];

  // History states
  const initialHistoryData = [
    {
      id: "TRX12345",
      dateTime: "09/03/2026\n15:30:00",
      senderName: "SparkMinds Technology",
      senderEmail: "uat-minds@spark.com",
      receiverName: "Nguyen Van A",
      paymentMethod: "Cash",
      sendAmount: "1,000.00 USD",
      status: "Approved",
    },
    {
      id: "TRX67890",
      dateTime: "08/03/2026\n10:15:22",
      senderName: "SparkMinds Technology",
      senderEmail: "uat-minds@spark.com",
      receiverName: "Le Thi B",
      paymentMethod: "Bank Transfer",
      sendAmount: "500.00 USD",
      status: "Pending",
    },
  ];

  const [historyData, setHistoryData] = useState(initialHistoryData);
  const [historyFilters, setHistoryFilters] = useState({
    id: "",
    method: "",
    status: "",
  });

  const handleHistorySearch = () => {
    let filtered = initialHistoryData.filter((item) => {
      const matchId = item.id
        .toLowerCase()
        .includes(historyFilters.id.toLowerCase());
      const matchMethod = historyFilters.method
        ? item.paymentMethod === historyFilters.method
        : true;
      const matchStatus = historyFilters.status
        ? item.status === historyFilters.status
        : true;
      return matchId && matchMethod && matchStatus;
    });
    setHistoryData(filtered);
  };

  const handleHistoryReset = () => {
    setHistoryFilters({ id: "", method: "", status: "" });
    setHistoryData(initialHistoryData);
  };

  if (!customer) return null;

  return (
    <div>
      <div className="d-flex align-items-center mb-4">
        <CommonButton
          variant="primary"
          className="me-3 d-flex align-items-center justify-content-center rounded-circle p-0"
          style={{ width: "50px", height: "50px", minWidth: "50px" }}
          onClick={() => navigate("/customer")}
        >
          <i className="fas fa-arrow-left"></i>
        </CommonButton>
        <h2 className="fw-bold text-black mb-0">Customer Details</h2>
      </div>

      <div className="row">
        <div className="col-xl-12">
          <div className="custom-tab-1">
            <Nav as="ul" className="nav nav-tabs custom-tabs-1 mb-4">
              <Nav.Item as="li">
                <Nav.Link
                  eventKey="information"
                  active={activeTab === "information"}
                  onClick={() => setActiveTab("information")}
                >
                  <i className="la la-user me-2"></i> Information
                </Nav.Link>
              </Nav.Item>
              <Nav.Item as="li">
                <Nav.Link
                  eventKey="beneficiary"
                  active={activeTab === "beneficiary"}
                  onClick={() => setActiveTab("beneficiary")}
                >
                  <i className="la la-users me-2"></i> Beneficiary
                </Nav.Link>
              </Nav.Item>
              <Nav.Item as="li">
                <Nav.Link
                  eventKey="history"
                  active={activeTab === "history"}
                  onClick={() => setActiveTab("history")}
                >
                  <i className="la la-history me-2"></i> Transaction History
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </div>

          <div className="py-3 rounded-4" style={{ minHeight: "600px" }}>
            {activeTab === "information" && (
              <InfoTab
                customer={customer}
                onEdit={() => {
                  if (customer.type === "Individual") {
                    setShowIndividualModal(true);
                  } else {
                    setShowOrganizationModal(true);
                  }
                }}
              />
            )}

            {activeTab === "beneficiary" && <BeneficiaryTab />}

            {activeTab === "history" && (
              <HistoryTab
                historyData={historyData}
                historyFilters={historyFilters}
                setHistoryFilters={setHistoryFilters}
                onSearch={handleHistorySearch}
                onReset={handleHistoryReset}
                onViewDetails={(item) => {
                  setSelectedTransaction(item);
                  setShowTransactionDetailsModal(true);
                }}
              />
            )}
          </div>
        </div>
      </div>

      <SendTransactionModal
        show={showSendTransactionModal}
        onHide={() => setShowSendTransactionModal(false)}
      />

      <SendBulkTransactionModal
        show={showSendBulkTransactionModal}
        onHide={() => setShowSendBulkTransactionModal(false)}
      />

      <TopUpModal
        show={showTopUpModal}
        onHide={() => setShowTopUpModal(false)}
        customer={customer}
      />

      <WithdrawalModal
        show={showWithdrawalModal}
        onHide={() => setShowWithdrawalModal(false)}
        customer={customer}
      />

      <TransactionDetailsModal
        show={showTransactionDetailsModal}
        onHide={() => setShowTransactionDetailsModal(false)}
        transaction={selectedTransaction}
      />

      <IndividualCustomerModal
        show={showIndividualModal}
        onHide={() => setShowIndividualModal(false)}
        customer={customer}
      />

      <OrganizationCustomerModal
        show={showOrganizationModal}
        onHide={() => setShowOrganizationModal(false)}
        customer={customer}
      />
    </div>
  );
};

export default CustomerDetails;
